import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Seo from "../components/Seo";
import {
  fetchBlogFeed,
  formatPublishedAt,
  parseTags,
  readingTime,
} from "../data/blogApi";
import {
  fadeUp,
  staggerContainer,
  staggerItem,
  easing,
} from "../utils/motionVariants";
import "../components/Section.css";
import "./BlogPage.css";

export default function BlogPage() {
  const [state, setState] = useState({ status: "loading", posts: [], error: null });
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading", posts: [], error: null });
    fetchBlogFeed({ limit: 30, signal: controller.signal })
      .then((data) => setState({ status: "ready", posts: data.posts, error: null }))
      .catch((err) => {
        if (err.name === "AbortError") return;
        setState({ status: "error", posts: [], error: err.message });
      });
    return () => controller.abort();
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    state.posts.forEach((p) => p.category && set.add(p.category));
    return ["All", ...Array.from(set).sort()];
  }, [state.posts]);

  const filtered = useMemo(() => {
    if (activeCategory === "All") return state.posts;
    return state.posts.filter((p) => p.category === activeCategory);
  }, [state.posts, activeCategory]);

  return (
    <section id="blog" className="section section-peach">
      <Seo
        title="Blog"
        path="/blog"
        description="Articles by Hirun Kolongahapitiya on software engineering, AI, automation, and product building - syndicated from the AEYE blog feed."
      />
      <motion.div
        className="section-header"
        initial="hidden"
        animate="visible"
        variants={fadeUp}
      >
        <h2>Guide & Articles</h2>
        <div className="section-underline" />
      </motion.div>

      {state.status === "ready" && categories.length > 1 && (
        <motion.div
          className="blog-tabs"
          role="tablist"
          aria-label="Blog categories"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4, ease: easing }}
        >
          {categories.map((cat) => (
            <motion.button
              key={cat}
              type="button"
              role="tab"
              aria-selected={activeCategory === cat}
              className={activeCategory === cat ? "active" : ""}
              onClick={() => setActiveCategory(cat)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.96 }}
            >
              {cat}
            </motion.button>
          ))}
        </motion.div>
      )}

      {state.status === "loading" && (
        <motion.p
          className="blog-status"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          Loading articles…
        </motion.p>
      )}

      {state.status === "error" && (
        <motion.div
          className="card blog-status blog-status-error"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <p style={{ margin: 0 }}>
            Couldn't load the blog feed right now. Please try again later.
          </p>
        </motion.div>
      )}

      {state.status === "ready" && filtered.length === 0 && (
        <motion.div
          className="card blog-status"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <p style={{ margin: 0 }}>
            No articles published yet. Check back soon - new posts go live as
            soon as they're written.
          </p>
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {state.status === "ready" && filtered.length > 0 && (
          <motion.div
            key={activeCategory}
            className="blog-grid"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
          >
            {filtered.map((post) => {
              const tags = parseTags(post.tags).slice(0, 3);
              const minutes = readingTime(post);
              return (
                <motion.article
                  key={post.slug}
                  className="blog-card"
                  variants={staggerItem}
                  whileHover={{ y: -8, boxShadow: "0 24px 50px rgba(15, 23, 42, 0.16)" }}
                  whileTap={{ scale: 0.985 }}
                  transition={{ duration: 0.25, ease: easing }}
                >
                  <Link to={`/blog/${post.slug}`} className="blog-card-link">
                    {post.featureImage && (
                      <div className="blog-card-cover">
                        <img
                          src={post.featureImage}
                          alt={post.title}
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="blog-card-body">
                    <div className="blog-card-meta">
                      {post.category && (
                        <span className="blog-chip">{post.category}</span>
                      )}
                      <time className="muted">
                        {formatPublishedAt(post.publishedAt)}
                      </time>
                      {minutes && <span className="muted">· {minutes}</span>}
                    </div>
                    <h3>{post.title}</h3>
                    {post.excerpt && <p className="muted">{post.excerpt}</p>}
                    {tags.length > 0 && (
                      <div className="blog-tags">
                        {tags.map((tag) => (
                          <span key={tag} className="blog-tag">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <span className="blog-read-more accent">Read article →</span>
                    </div>
                  </Link>
                </motion.article>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
